import * as Sentry from "@sentry/react";
import { blockBuyerBreadcrumb, blockBuyerRecordingEvent } from "./features/builder/lib/buyerPrivacy";
import { StrictMode, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import '@stackje/shared-components/style'
import '../../../packages/shared-components/src/globals.css'
import './index.css'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { AuthProvider } from './context/AuthProvider'
import { useAuth } from './hooks/useAuth'
import { Loading } from './components/shared/Loading'
import { Toaster } from '@stackje/shared-components/ui/toaster'
import { ColorSchemeProvider } from './context/ColorSchemeProvider'
import { FontSetProvider } from '@stackje/shared-components/context/FontSetProvider'
import { SentryErrorBoundary } from './components/shared/SentryErrorBoundary'
import { queryClient } from './lib/queryClient'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      beforeAddRecordingEvent: blockBuyerRecordingEvent,
    }),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  beforeBreadcrumb: blockBuyerBreadcrumb,
  // Session Replay
  replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enableLogs: true
});

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
  scrollRestoration: true,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          console.log(
            "Service Worker registered with scope:",
            registration.scope,
          );
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FontSetProvider>
          <ColorSchemeProvider>
            <InnerApp />
          </ColorSchemeProvider>
        </FontSetProvider>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
function InnerApp() {
  const auth = useAuth()
  if (auth.initialLoading) {
    return <Loading />
  }

  return (
    <SentryErrorBoundary>
      <RouterProvider router={router} context={{ auth }} />
    </SentryErrorBoundary>
  )
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}
